"use client";

import { useRef, useState, type ReactNode } from "react";

import {
  buildChartModel,
  CHART,
  NARROW_CHART,
  computeAltitudeStats,
  type AltitudeChartView,
  type AltitudeUnit,
} from "@tripcart/shared";

import { cx } from "../../cx";
import { DayReadout, SummaryTiles, UnitSwitch } from "./altitude-chart.parts";
import { ChartSvg } from "./altitude-chart.svg";
import styles from "./altitude-chart.module.css";
import {
  notifyUnitChanged,
  storeUnit,
  useIsNarrow,
  useStoredUnit,
} from "./use-chart-host";

/** Every fixed phrase the chart shows, so the host app can translate them. */
export interface AltitudeChartLabels {
  unit: string;
  highestPoint: string;
  totalAscent: string;
  totalDescent: string;
  startOfTour: string;
  noChange: string;
  chartAria: string;
}

export interface AltitudeChartProps {
  view: AltitudeChartView;
  labels: AltitudeChartLabels;
  /** The section heading and description, full width above the chart. */
  heading?: ReactNode;
  /** Remember the traveler's unit in their browser (spec §5.2). Off in the
   *  dashboard preview, which must show the app's unit. */
  persistUnit?: boolean;
  className?: string;
}

/** The unit on screen: the traveler's pick, else the one they chose on an
 *  earlier visit, else the agency's. */
function useChartUnit(
  agencyUnit: AltitudeUnit,
  persistUnit: boolean,
): [AltitudeUnit, (next: AltitudeUnit) => void] {
  const [picked, setPicked] = useState<AltitudeUnit | null>(null);
  const stored = useStoredUnit(persistUnit);
  const change = (next: AltitudeUnit) => {
    setPicked(next);
    if (!persistUnit) return;
    storeUnit(next);
    notifyUnitChanged();
  };
  return [picked ?? stored ?? agencyUnit, change];
}

export function AltitudeChart({
  view,
  labels,
  heading,
  persistUnit = true,
  className,
}: AltitudeChartProps) {
  const [unit, changeUnit] = useChartUnit(view.unit, persistUnit);
  const [active, setActive] = useState(-1);
  const hostRef = useRef<HTMLDivElement>(null);
  const narrow = useIsNarrow(hostRef);

  const model = buildChartModel(view.days, unit, narrow ? NARROW_CHART : CHART);
  const stats = computeAltitudeStats(view.days);

  return (
    <div className={cx(styles.root, className)}>
      {heading ? <div className={styles.head}>{heading}</div> : null}

      <div
        className={styles.chart}
        ref={hostRef}
        data-narrow={narrow ? "" : undefined}
      >
        <UnitSwitch
          unit={unit}
          unitLabel={labels.unit}
          onUnitChange={changeUnit}
        />
        <DayReadout
          index={active}
          day={active >= 0 ? view.days[active] : undefined}
          move={active >= 0 ? stats.perDay[active] : undefined}
          unit={unit}
          fmt={model.fmt}
          labels={labels}
          xPct={
            active >= 0
              ? (model.scale.colX(active + 0.5) / model.scale.frame.W) * 100
              : 0
          }
        />
        <ChartSvg
          model={model}
          active={active}
          narrow={narrow}
          ariaLabel={labels.chartAria}
          onActive={setActive}
        />
      </div>

      {view.showStats ? (
        <SummaryTiles
          stats={stats}
          unit={unit}
          fmt={model.fmt}
          labels={labels}
        />
      ) : null}
    </div>
  );
}
